import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkle, ChefHat, ArrowsMerge, SkipForward, ShieldCheck, Warning, PaperPlaneRight, ClockCounterClockwise, ArrowUUpLeft, Trash, LinkBreak } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_BADGE = {
  inconsistencia: { label: "Inconsistência de cadastro", cls: "bg-amber-100 text-amber-800" },
  duplicata: { label: "Duplicata de grafia", cls: "bg-blue-100 text-blue-800" },
  semantica: { label: "Análise semântica (IA)", cls: "bg-violet-100 text-violet-800" },
};

export default function SanitizeIngredientsModal({ open, onOpenChange, onMerged }) {
  const [tab, setTab] = useState("scan");
  const [history, setHistory] = useState(null);
  const [undoBusy, setUndoBusy] = useState(null);
  const [groups, setGroups] = useState(null);
  const [distinct, setDistinct] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [removeIds, setRemoveIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cmd, setCmd] = useState("");
  const [plan, setPlan] = useState(null);
  const [cmdBusy, setCmdBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [allIngs, setAllIngs] = useState([]);
  const [substituteId, setSubstituteId] = useState("");
  const [orphans, setOrphans] = useState(null);
  const [orphanBusy, setOrphanBusy] = useState(null);
  const [orphanMap, setOrphanMap] = useState({});

  const loadOrphans = async () => {
    try {
      const { data } = await axios.get(`${API}/ingredients/sanitize/orphans`);
      setOrphans(data.orphans || []);
    } catch (e) {
      console.warn("Auditoria de órfãos indisponível:", e?.message);
      setOrphans([]);
    }
  };

  const loadCatalog = async () => {
    try {
      const res = await axios.get(`${API}/ingredients`, { params: { page_size: 500 } });
      setAllIngs(Array.isArray(res.data) ? res.data : res.data.items || []);
    } catch (err) {
      console.warn("Catálogo indisponível:", err?.message);
    }
  };

  const resolveOrphans = async (resolutions, busyKey) => {
    setOrphanBusy(busyKey);
    try {
      const { data } = await axios.post(`${API}/ingredients/sanitize/orphans/resolve`, { resolutions });
      const created = data.resolved.filter((r) => r.created).length;
      const mapped = data.resolved.length - created;
      const sheets = data.resolved.reduce((acc, r) => acc + r.sheets_relinked, 0);
      const noPrice = resolutions.some((r) => r.action === "create" && !(r.new_ingredient?.purchase_price > 0));
      toast.success(`${created > 0 ? `${created} insumo(s) cadastrado(s)` : ""}${created > 0 && mapped > 0 ? ", " : ""}${mapped > 0 ? `${mapped} mapeado(s)` : ""} — ${sheets} ficha(s) revinculada(s) e CMV recalculado${created > 0 && noPrice ? ". Preço inicial será estimado automaticamente pelo SousChef" : ""}`);
      onMerged?.();
      await Promise.all([loadOrphans(), loadCatalog()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao resolver insumos órfãos");
    } finally {
      setOrphanBusy(null);
    }
  };

  const initGroup = (gs, n) => {
    const g = gs?.[n];
    const keep = g?.options.find((o) => o.recommended)?.id || g?.options[0]?.id || null;
    setSelected(keep);
    setRemoveIds((g?.suggested_remove_ids || []).filter((id) => id !== keep));
  };

  const scan = async () => {
    setBusy(true);
    try {
      const [{ data }] = await Promise.all([
        axios.post(`${API}/ingredients/deduplicate/scan`),
        loadOrphans(),
        loadCatalog(),
      ]);
      setGroups(data.groups);
      setDistinct(data.distinct_kept || []);
      setIdx(0);
      initGroup(data.groups, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na varredura");
    } finally {
      setBusy(false);
    }
  };

  const nextGroup = () => {
    const n = idx + 1;
    setIdx(n);
    initGroup(groups, n);
  };

  const doMerge = async (keepId, ids, after) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ingredients/deduplicate/merge`, { keep_id: keepId, remove_ids: ids });
      toast.success(`Fusão concluída: ${data.removed} removido(s), ${data.sheets_remapped} ficha(s) remapeada(s) e CMV recalculado`);
      onMerged?.();
      after?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na fusão");
    } finally {
      setBusy(false);
    }
  };

  const doPurge = async (ids, after) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ingredients/deduplicate/purge`, { ingredient_ids: ids });
      if (data.deleted_count > 0) {
        toast.success(`${data.deleted_count} insumo(s) excluído(s) — backup salvo no Histórico (Desfazer disponível)`);
        onMerged?.();
      }
      if (data.blocked?.length > 0) {
        setBlocked(data.blocked);
        setSubstituteId("");
        try {
          const res = await axios.get(`${API}/ingredients`, { params: { page_size: 500 } });
          const list = Array.isArray(res.data) ? res.data : res.data.items || [];
          const blockedIds = new Set(data.blocked.map((b) => b.id));
          setAllIngs(list.filter((i) => !blockedIds.has(i.id)));
        } catch (err) {
          console.warn("Lista de insumos indisponível p/ substituição:", err?.message);
        }
      } else {
        after?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na exclusão");
    } finally {
      setBusy(false);
    }
  };

  const sendCommand = async () => {
    if (!cmd.trim()) return;
    setCmdBusy(true);
    setPlan(null);
    try {
      const { data } = await axios.post(`${API}/ingredients/deduplicate/command`, { command: cmd });
      setPlan(data.plan);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não entendi o comando");
    } finally {
      setCmdBusy(false);
    }
  };

  const toggleRemove = (id) =>
    setRemoveIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectKeep = (id) => {
    setSelected(id);
    setRemoveIds((prev) => prev.filter((x) => x !== id));
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/ingredients/deduplicate/history`);
      setHistory(data.history);
    } catch (e) {
      toast.error("Erro ao carregar histórico");
    }
  };

  const undoMerge = async (logId) => {
    setUndoBusy(logId);
    try {
      const { data } = await axios.post(`${API}/ingredients/deduplicate/undo/${logId}`);
      toast.success(`Fusão desfeita: ${data.restored_ingredients} insumo(s) reativado(s), ${data.sheets_restored} ficha(s) re-vinculada(s) e CMV recalculado`);
      onMerged?.();
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao desfazer");
    } finally {
      setUndoBusy(null);
    }
  };

  const group = groups?.[idx];
  const badge = group ? TYPE_BADGE[group.type] || TYPE_BADGE.duplicata : null;

  const reset = () => { setGroups(null); setDistinct([]); setIdx(0); setPlan(null); setCmd(""); setTab("scan"); setHistory(null); setBlocked(null); setOrphans(null); setOrphanMap({}); };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-[660px] max-h-[85vh] overflow-y-auto" data-testid="sanitize-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChefHat size={20} className="text-amber-600" /> SousChef AI — Sanitização de Insumos
          </DialogTitle>
          <DialogDescription>Taxonomia gastronômica: funde apenas duplicatas reais, preserva insumos fisicamente distintos e recalcula o CMV no servidor</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-zinc-200 -mt-1" data-testid="sanitize-tabs">
          <button onClick={() => setTab("scan")}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 -mb-px ${tab === "scan" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
            data-testid="sanitize-tab-scan">
            ✨ Sanitizar
          </button>
          <button onClick={() => { setTab("history"); loadHistory(); }}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "history" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}
            data-testid="sanitize-tab-history">
            <ClockCounterClockwise size={14} /> Histórico de Sanitizações
          </button>
        </div>

        {tab === "history" ? (
          <div className="space-y-2" data-testid="sanitize-history-section">
            {history === null ? (
              <p className="text-sm text-zinc-400 py-4 text-center">Carregando histórico...</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-zinc-400 py-4 text-center border border-dashed border-zinc-200 rounded-lg">Nenhuma fusão registrada ainda.</p>
            ) : (
              history.map((h) => (
                <div key={h.id} className={`rounded-lg border p-3 ${h.status === "undone" ? "border-zinc-200 bg-zinc-50 opacity-70" : "border-zinc-200 bg-white"}`} data-testid={`merge-log-${h.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-zinc-900">
                        {h.action_type === "DELETION" ? (
                          <>
                            <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold mr-1.5">EXCLUSÃO</span>
                            <span className="text-red-600 line-through">{(h.removed_names || []).join(", ")}</span>
                          </>
                        ) : h.action_type === "ORPHAN_FIX" ? (
                          <>
                            <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-bold mr-1.5">ÓRFÃO</span>
                            <span className="text-zinc-600">{h.orphan_name}</span>
                            <span className="mx-1.5 text-zinc-400">→</span>
                            <span className="font-semibold text-emerald-700">{h.target_name}</span>
                            <span className="ml-1.5 text-[10px] text-zinc-400">({h.resolution_action === "create" ? "cadastrado" : "mapeado"})</span>
                          </>
                        ) : (
                          <>
                            <span className="text-red-600 line-through">{(h.removed_names || []).join(", ")}</span>
                            <span className="mx-1.5 text-zinc-400">→</span>
                            <span className="font-semibold text-emerald-700">{h.target_name}</span>
                          </>
                        )}
                      </p>
                      <p className="text-[11px] text-zinc-400 mt-0.5">
                        {new Date(h.created_at).toLocaleString("pt-BR")} · {(h.affected_recipe_ids || []).length} ficha(s) afetada(s)
                        {h.status === "undone" && <span className="ml-2 text-[10px] bg-zinc-200 text-zinc-600 px-1.5 py-0.5 rounded font-bold">DESFEITA</span>}
                      </p>
                    </div>
                    {h.status !== "undone" && (
                      <Button variant="outline" size="sm" className="flex-shrink-0 text-xs" onClick={() => undoMerge(h.id)} disabled={undoBusy === h.id} data-testid={`undo-merge-btn-${h.id}`}>
                        <ArrowUUpLeft size={13} className="mr-1" /> {undoBusy === h.id ? "Desfazendo..." : "Desfazer Fusão"}
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
            <p className="text-[10px] text-zinc-400 pt-1">O desfazer é 100% determinístico no banco (reativa o insumo original, re-vincula as fichas e recalcula o CMV) — sem consumo de créditos de IA.</p>
          </div>
        ) : groups === null ? (
          <div className="text-center py-6">
            <Sparkle size={36} className="mx-auto text-amber-500 mb-3" />
            <p className="text-sm text-zinc-600 mb-4">Varredura híbrida: fuzzy determinístico + análise semântica em lote (GPT-4o-mini, 1 chamada). Duplicatas reais separadas de insumos fisicamente distintos.</p>
            <Button onClick={scan} disabled={busy} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="sanitize-scan-btn">
              {busy ? "Analisando..." : "✨ Iniciar Varredura"}
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* ===== SEÇÃO 1: INCONSISTÊNCIAS & ERROS DE CADASTRO ===== */}
            <div data-testid="sanitize-inconsistencies-section">
              <p className="text-[11px] uppercase tracking-wider font-bold text-amber-700 flex items-center gap-1.5 mb-2">
                <Warning size={14} weight="bold" /> Inconsistências &amp; Erros de Cadastro
              </p>
              {!group ? (
                <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-center" data-testid="sanitize-done">
                  <p className="text-sm font-semibold text-zinc-900">
                    {groups.length === 0 ? "Nenhuma duplicata real encontrada — sua base está limpa! 👨‍🍳" : "Todos os grupos foram tratados. Base sanitizada!"}
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-wider text-zinc-400">Grupo {idx + 1} de {groups.length}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                  </div>
                  <div className="text-sm text-zinc-700 whitespace-pre-line mb-3" data-testid="sanitize-message">
                    {group.message.replace(/\*\*/g, "")}
                  </div>
                  <div className="space-y-2 mb-3">
                    {group.options.map((o, n) => (
                      <div key={o.id} className={`flex items-center gap-3 p-2.5 rounded-lg border text-sm bg-white ${selected === o.id ? "border-zinc-900" : "border-zinc-200"}`} data-testid={`sanitize-option-${n}`}>
                        <label className="flex items-center gap-2 cursor-pointer" title="Manter como insumo oficial">
                          <input type="radio" name="dedup-keep" checked={selected === o.id} onChange={() => selectKeep(o.id)} />
                          <span className="text-[10px] text-zinc-400 font-semibold">BASE</span>
                        </label>
                        <span className="flex-1 font-medium text-zinc-900">
                          {o.name}
                          {o.is_operational && <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold">nome operacional</span>}
                          {o.recommended && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">Recomendado</span>}
                        </span>
                        <span className="font-mono text-xs text-zinc-500">R$ {o.purchase_price.toFixed(2)}/{o.unit}</span>
                        <span className="text-xs text-zinc-400">{o.usage_count} ficha(s)</span>
                        <label className={`flex items-center gap-1 text-xs cursor-pointer ${selected === o.id ? "opacity-30 pointer-events-none" : ""}`} title="Marcar para eliminar nesta fusão">
                          <input type="checkbox" checked={removeIds.includes(o.id)} onChange={() => toggleRemove(o.id)} data-testid={`sanitize-remove-check-${n}`} />
                          <span className="text-red-600 font-semibold">fundir</span>
                        </label>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={nextGroup} disabled={busy} data-testid="sanitize-skip-btn">
                      <SkipForward size={15} className="mr-1.5" /> Pular grupo
                    </Button>
                    <Button variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" onClick={() => doPurge(removeIds, nextGroup)} disabled={busy || removeIds.length === 0} data-testid="sanitize-purge-btn">
                      <Trash size={15} className="mr-1.5" /> Excluir {removeIds.length} da base
                    </Button>
                    <Button className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" onClick={() => doMerge(selected, removeIds, nextGroup)} disabled={busy || !selected || removeIds.length === 0} data-testid="sanitize-merge-btn">
                      <ArrowsMerge size={15} className="mr-1.5" /> {busy ? "Processando..." : `Fundir ${removeIds.length} no oficial`}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* ===== SEÇÃO 2: INSUMOS DISTINTOS MANTIDOS ===== */}
            <div data-testid="sanitize-distinct-section">
              <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 flex items-center gap-1.5 mb-2">
                <ShieldCheck size={14} weight="bold" /> Insumos Distintos Mantidos
              </p>
              {distinct.length === 0 ? (
                <p className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-lg p-3">Nenhum conjunto de nomes parecidos precisou ser preservado por diferença técnica.</p>
              ) : (
                <div className="space-y-2">
                  {distinct.map((d) => (
                    <div key={d.names.join("|")} className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-2.5" data-testid={`sanitize-distinct-${d.names[0]}`}>
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        {d.names.map((nm) => (
                          <span key={nm} className="text-xs font-medium bg-white border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-800">{nm}</span>
                        ))}
                      </div>
                      <p className="text-[11px] text-emerald-800"><b>Diferem em:</b> {d.reason} · {d.detail}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ===== SEÇÃO 3: INSUMOS EM FICHAS TÉCNICAS PENDENTES DE CADASTRO (ÓRFÃOS) ===== */}
            <div data-testid="sanitize-orphans-section">
              <p className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 flex items-center gap-1.5 mb-2">
                <LinkBreak size={14} weight="bold" /> Insumos em Fichas Técnicas Pendentes de Cadastro
              </p>
              {orphans === null ? (
                <p className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-lg p-3">Auditando vínculos das fichas técnicas...</p>
              ) : orphans.length === 0 ? (
                <p className="text-xs text-zinc-500 border border-dashed border-emerald-200 bg-emerald-50/30 rounded-lg p-3" data-testid="sanitize-orphans-clean">
                  ✓ Todas as fichas técnicas estão com vínculos íntegros ao Catálogo Master.
                </p>
              ) : (
                <div className="space-y-2">
                  {orphans.map((o) => (
                    <div key={o.key} className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-2.5" data-testid={`sanitize-orphan-${o.key}`}>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-zinc-900">{o.name}</span>
                        <span className="text-[10px] bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded font-bold">sem vínculo no catálogo</span>
                        <span className="text-[11px] text-zinc-500">{o.quantity_sample ? `${o.quantity_sample} ` : ""}{o.unit}</span>
                      </div>
                      <p className="text-[11px] text-zinc-600 mb-2">
                        Aparece em: {o.sheets.map((s) => s.name).join(", ")}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" className="bg-indigo-700 text-white hover:bg-indigo-800 text-xs h-7"
                          disabled={orphanBusy !== null}
                          onClick={() => resolveOrphans([{ orphan_name: o.name, action: "create", new_ingredient: { unit: o.unit, quantity_purchased: 1, purchase_price: 0 } }], o.key)}
                          data-testid={`orphan-create-btn-${o.key}`}>
                          {orphanBusy === o.key ? "Processando..." : "Cadastrar no Catálogo Master"}
                        </Button>
                        <select value={orphanMap[o.key] || ""} onChange={(e) => setOrphanMap((m) => ({ ...m, [o.key]: e.target.value }))}
                          className="flex-1 min-w-[170px] text-xs border border-zinc-300 rounded-md px-2 py-1.5 bg-white"
                          data-testid={`orphan-map-select-${o.key}`}>
                          <option value="">Mapear para insumo existente...</option>
                          {o.suggestions.length > 0 && (
                            <optgroup label="Sugestões (parecidos)">
                              {o.suggestions.map((s) => <option key={`sug-${s.id}`} value={s.id}>{s.name}</option>)}
                            </optgroup>
                          )}
                          <optgroup label="Catálogo completo">
                            {allIngs.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                          </optgroup>
                        </select>
                        <Button variant="outline" size="sm" className="text-xs h-7"
                          disabled={orphanBusy !== null || !orphanMap[o.key]}
                          onClick={() => resolveOrphans([{ orphan_name: o.name, action: "map", target_ingredient_id: orphanMap[o.key] }], o.key)}
                          data-testid={`orphan-map-btn-${o.key}`}>
                          Mapear
                        </Button>
                      </div>
                    </div>
                  ))}
                  {orphans.length > 1 && (
                    <Button size="sm" variant="outline" className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      disabled={orphanBusy !== null}
                      onClick={() => resolveOrphans(orphans.map((o) => ({ orphan_name: o.name, action: "create", new_ingredient: { unit: o.unit, quantity_purchased: 1, purchase_price: 0 } })), "all")}
                      data-testid="orphan-create-all-btn">
                      {orphanBusy === "all" ? "Cadastrando todos..." : `Cadastrar todos os ${orphans.length} novos insumos no Catálogo Master`}
                    </Button>
                  )}
                  <p className="text-[10px] text-zinc-400">Cada resolução revincula os ponteiros das fichas, recalcula o CMV no servidor e fica no Histórico com Desfazer determinístico.</p>
                </div>
              )}
            </div>

            {/* ===== TRAVA DE SEGURANÇA: INSUMOS VINCULADOS A FICHAS ===== */}
            {blocked && blocked.length > 0 && (
              <div className="rounded-lg border-2 border-red-300 bg-red-50/50 p-3" data-testid="sanitize-blocked-panel">
                <p className="text-sm font-bold text-red-700 flex items-center gap-1.5 mb-2">
                  <Warning size={15} weight="bold" /> Exclusão bloqueada — insumo(s) em uso em Fichas Técnicas
                </p>
                <div className="space-y-2 mb-3">
                  {blocked.map((b) => (
                    <div key={b.id} className="text-xs text-zinc-700 bg-white border border-red-100 rounded p-2">
                      <b>{b.name}</b> é utilizado em: {b.sheets.map((s) => s.name).join(", ")}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-zinc-600 mb-2">O que deseja fazer? <b>(a)</b> Substituir por outro insumo antes de apagar (fichas remapeadas + CMV recalculado) ou <b>(b)</b> cancelar a exclusão.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={substituteId} onChange={(e) => setSubstituteId(e.target.value)}
                    className="flex-1 min-w-[180px] text-sm border border-zinc-300 rounded-md px-2 py-1.5 bg-white"
                    data-testid="sanitize-substitute-select">
                    <option value="">Escolher insumo substituto...</option>
                    {allIngs.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                  <Button size="sm" className="bg-zinc-950 text-white hover:bg-zinc-800" disabled={busy || !substituteId}
                    onClick={() => doMerge(substituteId, blocked.map((b) => b.id), () => setBlocked(null))}
                    data-testid="sanitize-substitute-btn">
                    (a) Substituir e excluir
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setBlocked(null)} data-testid="sanitize-blocked-cancel-btn">
                    (b) Cancelar
                  </Button>
                </div>
              </div>
            )}

            {/* ===== DIÁLOGO LIVRE (COMANDO EM LINGUAGEM NATURAL) ===== */}
            <div className="border-t border-zinc-200 pt-4" data-testid="sanitize-command-section">
              <p className="text-[11px] uppercase tracking-wider font-bold text-zinc-500 mb-2">💬 Comando livre ao SousChef</p>
              <Textarea
                value={cmd}
                onChange={(e) => setCmd(e.target.value)}
                placeholder={`Ex: Funda "camarão p/ risoto" no "Camarão Cinza Limpo M" · ou · Exclua os insumos "teste 1" e "farinha vencida", eles não pertencem à nossa base`}
                className="text-sm min-h-[64px]"
                data-testid="sanitize-command-input"
              />
              <Button variant="outline" size="sm" className="mt-2" onClick={sendCommand} disabled={cmdBusy || !cmd.trim()} data-testid="sanitize-command-send-btn">
                <PaperPlaneRight size={14} className="mr-1.5" /> {cmdBusy ? "Interpretando..." : "Enviar comando"}
              </Button>

              {plan && (
                <div className="mt-3 rounded-lg border border-zinc-300 bg-zinc-50 p-3" data-testid="sanitize-plan">
                  <p className="text-sm font-semibold text-zinc-900 mb-1">
                    {plan.action === "delete" ? "🗑️ Plano de exclusão (Purga de Resíduos)" : "Plano de fusão proposto"}
                  </p>
                  <p className="text-xs text-zinc-600 mb-2">{plan.summary}</p>
                  {plan.keep && <p className="text-xs"><b>Base (mantém):</b> <span className="text-emerald-700 font-semibold">{plan.keep.name}</span></p>}
                  <p className="text-xs"><b>{plan.action === "delete" ? "Excluir da base:" : "Eliminar & remapear:"}</b> <span className="text-red-600">{plan.remove.map((r) => r.name).join(", ")}</span></p>
                  {plan.untouched?.length > 0 && <p className="text-xs"><b>Intocados:</b> {plan.untouched.join(", ")}</p>}
                  {plan.warnings?.map((w, n) => (
                    <p key={n} className="text-[11px] text-amber-700 mt-1 flex items-start gap-1"><Warning size={12} className="mt-0.5 flex-shrink-0" /> {w}</p>
                  ))}
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setPlan(null)} data-testid="sanitize-plan-cancel-btn">Cancelar</Button>
                    <Button size="sm" className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" disabled={busy}
                      onClick={() => {
                        const ids = plan.remove.map((r) => r.id);
                        const after = () => { setPlan(null); setCmd(""); scan(); };
                        if (plan.action === "delete") doPurge(ids, after);
                        else doMerge(plan.keep.id, ids, after);
                        if (plan.action === "delete") setPlan(null);
                      }}
                      data-testid="sanitize-plan-confirm-btn">
                      {busy ? "Executando..." : plan.action === "delete" ? "Confirmar exclusão" : "Confirmar fusão"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
